// Crypto Wallets → Configuration → Collection Policies (SM-278).
// One policy per asset: when a deposit address holds enough of the
// asset, a sweep moves it into the collection wallet. The list shows
// every stored policy and which deposit-enabled assets still lack one;
// create excludes covered assets (backend 409 is the hard guard) and
// edit locks the asset.
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { FormModal } from "@/components/ui/FormModal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { TokenAmountInput } from "@/components/ui/TokenAmountInput";
import {
  EnabledToggle,
  errMsg,
  td,
  th,
  useCanConfig,
} from "@/components/wallet-ops/configShared";
import {
  CoverageLine,
  DurationField,
  fmtDurationMs,
  PolicyLoadError,
  PolicyPageHeader,
  PolicyStatusBadge,
  RuleAmount,
  UncoveredCallout,
} from "@/components/wallet-ops/policyShared";
import {
  createCollectionConfig,
  fetchCollectionConfigs,
  fetchCryptoAssets,
  putCollectionConfig,
  walletKeys,
} from "@/lib/wallet-queries";
import type { CryptoAsset, CryptoCollectionConfig } from "@/lib/wallet-types";
import { fmtRelative } from "@/lib/format";

export const Route = createFileRoute(
  "/_app/wallet-ops/config/policies/collection",
)({
  component: CollectionPoliciesPage,
});

type EditorState =
  | { mode: "create" }
  | { mode: "edit"; cfg: CryptoCollectionConfig }
  | null;

function CollectionPoliciesPage() {
  const canConfig = useCanConfig();
  const assets = useQuery({
    queryKey: walletKeys.assets(),
    queryFn: () => fetchCryptoAssets(),
  });
  const configs = useQuery({
    queryKey: walletKeys.collectionConfigs(),
    queryFn: fetchCollectionConfigs,
  });
  const [editor, setEditor] = useState<EditorState>(null);

  const allAssets = assets.data ?? [];
  const rows = configs.data ?? [];
  const covered = new Set(rows.map((c) => c.asset_id));
  const assetByID = (id: string) => allAssets.find((a) => a.asset_id === id);

  const creatable = allAssets.filter((a) => !covered.has(a.asset_id));
  // Deposits are what accumulate on addresses, so deposit-enabled assets
  // are the ones a missing sweep policy actually hurts.
  const uncoveredDeposit = allAssets.filter(
    (a) => a.deposit_enabled && !covered.has(a.asset_id),
  );
  const depositCovered = allAssets.filter(
    (a) => a.deposit_enabled && covered.has(a.asset_id),
  ).length;
  const pausedCount = rows.filter((c) => !c.enabled).length;

  return (
    <div className="space-y-6">
      <PolicyPageHeader
        title="Collection policies"
        definition={
          <>
            Sweeps deposit-address balances once they're worth moving. The
            hot wallet is governed separately by its Hot Liquidity policy:
            deposits top it up toward that policy's target, and only the
            excess above that policy's maximum is ever swept. One policy
            per asset; an asset without a policy is never swept.
          </>
        }
        steps={[
          "check balances on schedule",
          "address holds at least the threshold?",
          "hot below its liquidity target? sweep tops it up — else sweep to the collection wallet",
          "leave the remainder · skip if fee exceeds cap",
        ]}
      />

      <div className="space-y-3">
        <CoverageLine
          covered={depositCovered}
          total={allAssets.filter((a) => a.deposit_enabled).length}
          noun="deposit-enabled asset"
          pausedCount={pausedCount}
        />
        <UncoveredCallout
          names={uncoveredDeposit.map((a) => a.symbol)}
          consequence="deposits of these assets pile up on user addresses and are never moved to the collection wallet."
        />
        <Card>
          <CardHeader className="border-b border-border">
            <CardTitle>Policies</CardTitle>
            {canConfig && (
              <Button size="sm" onClick={() => setEditor({ mode: "create" })}>
                New policy
              </Button>
            )}
          </CardHeader>
          <CardBody className="p-0">
            {configs.isLoading ? (
              <div className="space-y-2 p-5">
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
              </div>
            ) : configs.isError ? (
              <PolicyLoadError
                message={errMsg(configs.error)}
                onRetry={() => void configs.refetch()}
              />
            ) : rows.length === 0 ? (
              <EmptyState
                className="m-5"
                title="No collection policies yet"
                description="Deposits stay on each user's address until a sweep collects them. Until an asset has a policy, its deposits are never moved to the collection wallet."
                action={
                  canConfig && (
                    <Button
                      size="sm"
                      onClick={() => setEditor({ mode: "create" })}
                    >
                      New policy
                    </Button>
                  )
                }
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-border bg-surface-2">
                    <tr>
                      <th className={th}>Asset</th>
                      <th className={th}>Rule</th>
                      <th className={th}>Status</th>
                      <th className={th}>Updated</th>
                      <th className={th} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((c) => {
                      const asset = assetByID(c.asset_id);
                      const dec = asset?.token_decimals ?? 18;
                      return (
                        <tr
                          key={c.asset_id}
                          className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-2"
                          onClick={() => setEditor({ mode: "edit", cfg: c })}
                        >
                          <td className={td}>
                            <div className="font-medium text-t-1">
                              {asset?.symbol ?? c.asset_id}
                            </div>
                            <div className="font-mono text-2xs text-muted">
                              {c.asset_id}
                            </div>
                          </td>
                          <td className={td}>
                            <span className="text-t-2">
                              at{" "}
                              <RuleAmount
                                base={c.threshold_base}
                                decimals={dec}
                                symbol={asset?.symbol}
                              />{" "}
                              sweep · leave{" "}
                              <RuleAmount
                                base={c.remain_base}
                                decimals={dec}
                                symbol={asset?.symbol}
                                none="0"
                              />{" "}
                              · every {fmtDurationMs(c.interval_ms)}
                            </span>
                          </td>
                          <td className={td}>
                            <PolicyStatusBadge enabled={c.enabled} />
                          </td>
                          <td className={`${td} text-muted`}>
                            {c.updated_at ? fmtRelative(c.updated_at) : "—"}
                          </td>
                          <td className={`${td} text-right`}>
                            {canConfig && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditor({ mode: "edit", cfg: c });
                                }}
                              >
                                Edit
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
        <p className="text-2xs text-muted">
          Paused means the scanner skips the asset — deposits stay on user
          addresses until the policy is re-enabled. Sweep runs appear under
          Custody → Collections.
        </p>
      </div>

      <CollectionPolicyEditor
        state={editor}
        creatable={creatable}
        assetByID={assetByID}
        onClose={() => setEditor(null)}
      />
    </div>
  );
}

function CollectionPolicyEditor({
  state,
  creatable,
  assetByID,
  onClose,
}: {
  state: EditorState;
  creatable: CryptoAsset[];
  assetByID: (id: string) => CryptoAsset | undefined;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const open = state !== null;
  const editing = state?.mode === "edit" ? state.cfg : null;
  const [draft, setDraft] = useState<CryptoCollectionConfig>(
    blankCollectionConfig(),
  );
  const draftKey =
    state === null ? "closed" : state.mode === "create" ? "create" : state.cfg.asset_id;
  const [lastKey, setLastKey] = useState("closed");
  if (draftKey !== lastKey) {
    setLastKey(draftKey);
    setDraft(editing ? { ...editing } : blankCollectionConfig());
  }

  const save = useMutation({
    mutationFn: (input: CryptoCollectionConfig) =>
      editing ? putCollectionConfig(input) : createCollectionConfig(input),
    onSuccess: async () => {
      onClose();
      await qc.invalidateQueries({ queryKey: walletKeys.collectionConfigs() });
      await qc.invalidateQueries({
        queryKey: walletKeys.collectionConfig(draft.asset_id),
      });
    },
    meta: {
      successMessage: editing
        ? "Collection policy updated."
        : "Collection policy created.",
    },
  });

  const set = <K extends keyof CryptoCollectionConfig>(
    k: K,
    v: CryptoCollectionConfig[K],
  ) => setDraft((d) => ({ ...d, [k]: v }));

  const asset = assetByID(draft.asset_id);
  const dec = asset?.token_decimals ?? 18;
  const canSubmit =
    draft.asset_id !== "" && draft.threshold_base !== "" && draft.interval_ms > 0;

  return (
    <FormModal
      open={open}
      title={
        editing
          ? `Edit collection policy — ${assetByID(editing.asset_id)?.symbol ?? editing.asset_id}`
          : "New collection policy"
      }
      description={
        editing
          ? "Changes apply from the next sweep pass."
          : "Picks are limited to assets without a policy — each asset has at most one."
      }
      confirmLabel={editing ? "Save changes" : "Create policy"}
      loading={save.isPending}
      error={save.isError ? errMsg(save.error) : null}
      canSubmit={canSubmit}
      onSubmit={() => save.mutate(draft)}
      onCancel={() => {
        onClose();
        save.reset();
      }}
    >
      <div className="space-y-4">
        {editing ? (
          <Input
            label="Asset"
            value={`${assetByID(editing.asset_id)?.symbol ?? editing.asset_id} (${editing.asset_id})`}
            disabled
            hint="A policy stays with its asset — create a separate policy for another one."
          />
        ) : (
          <Select
            label="Asset"
            placeholder="Pick an asset"
            value={draft.asset_id}
            onChange={(e) => set("asset_id", e.target.value)}
            options={creatable.map((a) => ({
              value: a.asset_id,
              label: `${a.symbol} · ${a.asset_id}`,
            }))}
            hint={
              creatable.length === 0
                ? "Every asset already has a policy — edit one from the list instead."
                : "Assets that already have a policy aren't listed."
            }
          />
        )}
        <TokenAmountInput
          label="Sweep when an address holds at least"
          symbol={asset?.symbol}
          base={draft.threshold_base}
          decimals={dec}
          onBase={(b) => set("threshold_base", b)}
          allowEmpty
          emptyHint="Required — balances under this stay on the address."
        />
        <TokenAmountInput
          label="Leave behind after the sweep"
          symbol={asset?.symbol}
          base={draft.remain_base}
          decimals={dec}
          onBase={(b) => set("remain_base", b)}
          allowEmpty
          emptyHint="empty = sweep deposit addresses completely."
        />
        <p className="text-2xs text-muted">
          Hot-wallet handling (alert floor, replenish target, retained
          reserve) lives in Configuration → Hot Liquidity Policies — this
          policy only governs when balances are worth sweeping.
        </p>
        <TokenAmountInput
          label="Skip if network fee exceeds"
          symbol={asset?.symbol}
          base={draft.max_fee_base}
          decimals={dec}
          onBase={(b) => set("max_fee_base", b)}
          allowEmpty
          emptyHint="empty = no cap; sweeps go out whatever the fee."
        />
        <DurationField
          label="Check every"
          ms={draft.interval_ms}
          onMs={(ms) => set("interval_ms", ms)}
        />
        <EnabledToggle
          checked={draft.enabled}
          onChange={(v) => set("enabled", v)}
        />
        <p className="text-2xs text-muted">
          Off = the scanner skips this asset; the policy is kept but nothing
          is swept.
        </p>
      </div>
    </FormModal>
  );
}

function blankCollectionConfig(): CryptoCollectionConfig {
  return {
    asset_id: "",
    threshold_base: "",
    remain_base: "",
    max_fee_base: "",
    interval_ms: 3_600_000,
    enabled: true,
  };
}
